/**
 * Wave 29 Chunk 8 — Public-holiday helpers + lazy hook.
 *
 * Loads SA public holidays for ±1 year once per session, caches them in
 * memory, and exposes `useHolidays()` / `isHoliday(dateIso)` helpers
 * usable anywhere we need to warn (calendar dot, JC date picker, etc.).
 */
import { useEffect, useState } from "react";
import api from "./api";

let _cache = null;
let _loading = null;

export async function loadHolidays() {
  if (_cache) return _cache;
  if (_loading) return _loading;
  _loading = api.get("/calendar/holidays")
    .then((r) => {
      _cache = (r.data?.items || []).reduce((acc, h) => {
        acc[h.date] = h;
        return acc;
      }, {});
      return _cache;
    })
    .catch(() => { _cache = {}; return _cache; })
    .finally(() => { _loading = null; });
  return _loading;
}

export function isHoliday(dateIso) {
  if (!dateIso || !_cache) return null;
  return _cache[dateIso] || null;
}

export function useHolidays() {
  const [, force] = useState(0);
  useEffect(() => {
    if (_cache) return;
    loadHolidays().then(() => force((x) => x + 1));
  }, []);
  return { isHoliday, holidays: _cache || {} };
}

/** Convenience: returns the holiday object if dateIso falls on one, else null.
 *  Use this in date pickers / dialogs to surface a warning. */
export function holidayWarning(dateIso) {
  const h = isHoliday(dateIso);
  if (!h) return null;
  return `${h.name} — public holiday`;
}
